import { useState } from 'react';
import { useParams } from 'react-router';
import { Loader2, AlertCircle, CheckCircle2 } from 'lucide-react';
import appliBg from '../../assets/appliBg.svg';
import { Button } from '../../components/ui/button';
import { useGetEmployerReferenceQuery, useSubmitEmployerReferenceMutation } from '../../store/api/apiSlice';
import type { EmployerReferenceRating } from '../../types';

function readableError(err: any, fallback: string): string {
    const msg = err?.data?.message ?? err?.message;
    if (Array.isArray(msg)) return msg.join(', ');
    return typeof msg === 'string' ? msg : fallback;
}

// Shared page shell so every state (loading / invalid / done / form) looks the same.
function Shell({ children }: { children: React.ReactNode }) {
    return (
        <div className="flex min-h-screen items-center justify-center bg-[#f5f5f5] p-4 md:p-10">
            <div className="relative w-full max-w-[760px] overflow-hidden rounded-2xl bg-white md:rounded-3xl">
                <img
                    src={appliBg}
                    alt=""
                    className="absolute right-0 top-0 hidden h-[260px] w-auto rounded-tr-3xl object-contain lg:block"
                />
                <div
                    className="absolute right-0 top-0 hidden h-[260px] w-[460px] rounded-tr-3xl lg:block"
                    style={{
                        backgroundImage:
                            'linear-gradient(92deg, rgb(255,255,255) 14%, rgba(255,255,255,0.886) 48%, rgba(255,255,255,0) 110%)',
                    }}
                />
                <div className="relative z-10 px-6 py-10 md:px-12 md:py-12">{children}</div>
            </div>
        </div>
    );
}

const RATING_OPTIONS: { value: EmployerReferenceRating; label: string }[] = [
    { value: 'excellent', label: 'Excellent' },
    { value: 'good', label: 'Good' },
    { value: 'bad', label: 'Bad' },
];

function RatingField({
    label,
    value,
    onChange,
}: {
    label: string;
    value: EmployerReferenceRating | null;
    onChange: (v: EmployerReferenceRating) => void;
}) {
    return (
        <div className="flex flex-col gap-2">
            <span className="text-sm font-semibold text-[#1b1b1b]">
                {label} <span className="text-red-500">*</span>
            </span>
            <div className="flex gap-2">
                {RATING_OPTIONS.map((opt) => {
                    const active = value === opt.value;
                    return (
                        <button
                            key={opt.value}
                            type="button"
                            onClick={() => onChange(opt.value)}
                            className={`flex-1 rounded-lg border px-3 py-2.5 text-sm font-medium transition-colors ${
                                active
                                    ? 'border-[#FF6B35] bg-[#FF6B35]/10 text-[#FF6B35]'
                                    : 'border-[#e0e0e0] text-[#4e4d4d] hover:border-[#c9c9c9]'
                            }`}
                        >
                            {opt.label}
                        </button>
                    );
                })}
            </div>
        </div>
    );
}

function TextField({
    label,
    value,
    onChange,
    placeholder,
    multiline,
}: {
    label: string;
    value: string;
    onChange: (v: string) => void;
    placeholder?: string;
    multiline?: boolean;
}) {
    return (
        <label className="flex flex-col gap-1.5">
            <span className="text-sm font-semibold text-[#1b1b1b]">{label}</span>
            {multiline ? (
                <textarea
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    rows={3}
                    placeholder={placeholder}
                    className="w-full resize-y rounded-lg border border-[#e0e0e0] px-3 py-2 text-sm outline-none focus:border-[#FF6B35] focus:ring-2 focus:ring-[#FF6B35]/15"
                />
            ) : (
                <input
                    type="text"
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    placeholder={placeholder}
                    className="w-full rounded-lg border border-[#e0e0e0] h-11 px-3 text-sm outline-none focus:border-[#FF6B35] focus:ring-2 focus:ring-[#FF6B35]/15"
                />
            )}
        </label>
    );
}

export default function EmployerReference() {
    const { token = '' } = useParams();
    const { data, isLoading, isError } = useGetEmployerReferenceQuery(token, { skip: !token });
    const [submit, { isLoading: isSubmitting }] = useSubmitEmployerReferenceMutation();

    const [positionHeld, setPositionHeld] = useState('');
    const [periodOfEmployment, setPeriodOfEmployment] = useState('');
    const [workPerformance, setWorkPerformance] = useState<EmployerReferenceRating | null>(null);
    const [professionalConduct, setProfessionalConduct] = useState<EmployerReferenceRating | null>(null);
    const [abilityToAdapt, setAbilityToAdapt] = useState<EmployerReferenceRating | null>(null);
    const [abilityToLearnAndImprove, setAbilityToLearnAndImprove] = useState<EmployerReferenceRating | null>(null);
    const [disciplinaryActions, setDisciplinaryActions] = useState('');
    const [reasonForLeaving, setReasonForLeaving] = useState('');
    const [generalRemarks, setGeneralRemarks] = useState('');
    const [error, setError] = useState('');
    const [done, setDone] = useState(false);

    if (isLoading) {
        return (
            <div className="flex min-h-screen items-center justify-center bg-[#f5f5f5]">
                <Loader2 className="h-10 w-10 animate-spin text-[#FF6B35]" />
            </div>
        );
    }

    if (isError || !data) {
        return (
            <Shell>
                <div className="flex flex-col items-center gap-3 py-6 text-center">
                    <AlertCircle className="h-12 w-12 text-red-500" />
                    <h1 className="text-xl font-bold text-[#1b1b1b]">This link is invalid</h1>
                    <p className="max-w-md text-sm text-[#4e4d4d]">
                        The reference link is invalid or has expired. If you believe this is a mistake, please contact
                        the person who sent it to you.
                    </p>
                </div>
            </Shell>
        );
    }

    // Already responded (either loaded that way or just submitted) → thank-you.
    if (done || data.status === 'responded') {
        return (
            <Shell>
                <div className="flex flex-col items-center gap-3 py-6 text-center">
                    <CheckCircle2 className="h-12 w-12 text-green-600" />
                    <h1 className="text-xl font-bold text-[#1b1b1b]">Thank you</h1>
                    <p className="max-w-md text-sm text-[#4e4d4d]">
                        Your reference for {data.employeeName} has been received. There is nothing more to do. You can
                        close this page.
                    </p>
                </div>
            </Shell>
        );
    }

    const handleSubmit = async () => {
        setError('');
        if (!workPerformance || !professionalConduct || !abilityToAdapt || !abilityToLearnAndImprove) {
            setError(
                'Please rate all four areas (Work performance, Professional conduct, Ability to adapt, Ability to learn and improve).',
            );
            return;
        }
        try {
            await submit({
                token,
                workPerformance,
                professionalConduct,
                abilityToAdapt,
                abilityToLearnAndImprove,
                ...(positionHeld.trim() ? { positionHeld: positionHeld.trim() } : {}),
                ...(periodOfEmployment.trim() ? { periodOfEmployment: periodOfEmployment.trim() } : {}),
                ...(disciplinaryActions.trim() ? { disciplinaryActions: disciplinaryActions.trim() } : {}),
                ...(reasonForLeaving.trim() ? { reasonForLeaving: reasonForLeaving.trim() } : {}),
                ...(generalRemarks.trim() ? { generalRemarks: generalRemarks.trim() } : {}),
            }).unwrap();
            setDone(true);
        } catch (err) {
            setError(readableError(err, 'Could not submit your reference. Please try again.'));
        }
    };

    return (
        <Shell>
            <div className="flex flex-col gap-6">
                <div>
                    <p className="text-sm font-semibold uppercase tracking-wide text-[#FF6B35]">Employment reference</p>
                    <h1 className="mt-1 text-2xl font-bold text-[#1b1b1b]">Reference for {data.employeeName}</h1>
                    <p className="mt-2 text-sm text-[#4e4d4d]">
                        {data.employeeName} listed <span className="font-semibold">{data.employerName}</span>
                        {data.declaredPosition ? (
                            <>
                                {' '}
                                as a previous employer, where they worked as{' '}
                                <span className="font-semibold">{data.declaredPosition}</span>.
                            </>
                        ) : (
                            <> as a previous employer.</>
                        )}{' '}
                        Please share a brief, honest reference. It takes under two minutes.
                    </p>
                </div>

                <div className="flex flex-col gap-4">
                    <div className="flex flex-col gap-4 sm:flex-row">
                        <div className="flex-1">
                            <TextField
                                label="Position held"
                                value={positionHeld}
                                onChange={setPositionHeld}
                                placeholder={data.declaredPosition ?? 'e.g. Analyst'}
                            />
                        </div>
                        <div className="flex-1">
                            <TextField
                                label="Period of employment"
                                value={periodOfEmployment}
                                onChange={setPeriodOfEmployment}
                                placeholder="e.g. Jan 2020 – Mar 2023"
                            />
                        </div>
                    </div>

                    <RatingField label="Work performance" value={workPerformance} onChange={setWorkPerformance} />
                    <RatingField
                        label="Professional conduct"
                        value={professionalConduct}
                        onChange={setProfessionalConduct}
                    />
                    <RatingField label="Ability to adapt" value={abilityToAdapt} onChange={setAbilityToAdapt} />
                    <RatingField
                        label="Ability to learn and improve"
                        value={abilityToLearnAndImprove}
                        onChange={setAbilityToLearnAndImprove}
                    />

                    <TextField
                        label="Any disciplinary actions?"
                        value={disciplinaryActions}
                        onChange={setDisciplinaryActions}
                        placeholder="Describe any disciplinary actions, or leave blank if none"
                        multiline
                    />

                    <TextField
                        label="Reason for leaving"
                        value={reasonForLeaving}
                        onChange={setReasonForLeaving}
                        placeholder="e.g. Career move"
                    />
                    <TextField
                        label="General remarks"
                        value={generalRemarks}
                        onChange={setGeneralRemarks}
                        placeholder="Anything else you'd like to add"
                        multiline
                    />
                </div>

                {error && (
                    <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                        {error}
                    </div>
                )}

                <Button
                    type="button"
                    onClick={handleSubmit}
                    disabled={isSubmitting}
                    className="h-12 w-full bg-[#FF6B35] text-white hover:bg-[#FF5722] disabled:opacity-60"
                >
                    {isSubmitting ? (
                        <span className="flex items-center justify-center gap-2">
                            <Loader2 className="h-4 w-4 animate-spin" /> Submitting…
                        </span>
                    ) : (
                        'Submit reference'
                    )}
                </Button>
            </div>
        </Shell>
    );
}
